import { usePreferences } from '../theme/PreferencesContext';
import { LANGUAGES } from '../i18n';

interface LanguageSwitchProps {
  /**
   * 'segmented' — the pill pair from the mobile app's Settings screen, for a
   * settings panel. 'compact' — the same choice sized for a dropdown menu.
   */
  variant?: 'segmented' | 'compact';
  /** Called after the language changes, e.g. to close the menu it lives in. */
  onChange?: () => void;
}

/**
 * The language switch — a direct port of the two-button control in
 * mobileapp/src/screens/settings/SettingsScreen.tsx. Both options are always
 * visible (rather than a dropdown of one) because that is what makes the
 * control usable to someone who can't read the language currently selected.
 *
 * Applies immediately, with no Save button: the change is local-first and the
 * server sync is fire-and-forget (see theme/PreferencesContext), so there is
 * nothing to wait for and nothing to confirm.
 */
export const LanguageSwitch = ({ variant = 'segmented', onChange }: LanguageSwitchProps) => {
  const { language, setLanguage } = usePreferences();

  return (
    <div
      className={`lang-switch lang-switch--${variant}`}
      role="group"
      aria-label="Language"
    >
      {LANGUAGES.map((option) => {
        const selected = option.id === language;
        return (
          <button
            key={option.id}
            type="button"
            className={`lang-switch__option${selected ? ' is-active' : ''}`}
            aria-pressed={selected}
            onClick={() => {
              if (!selected) setLanguage(option.id);
              onChange?.();
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
